import React, { FunctionComponent } from "react";

import XCloseIcon from "@uui/XClose";

import MuiCard, { CardProps } from "@mui/material/Card";
import type { IconButtonProps } from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import IconButton from "@rui/IconButton";
import Stack, { StackProps } from "@rui/Stack";

interface FeaturePopupCardProps extends CardProps {
  layout?: "horizontal" | "vertical";
}

interface StackLayoutProps extends StackProps {
  layout?: "horizontal" | "vertical";
}

export const FeaturePopupCard = styled<
  FunctionComponent<FeaturePopupCardProps>
>(({ children, layout = "vertical", ...props }) => (
  <MuiCard {...props}>
    <Stack
      direction={{
        xxs: "column",
        sm: layout === "horizontal" ? "row" : "column",
      }}
      height="100%"
    >
      {children}
    </Stack>
  </MuiCard>
))(({ theme, layout }) => ({
  position: "absolute",
  height: "500px",
  width: "398px",
  [theme.breakpoints.up("sm")]: {
    height: layout === "horizontal" ? "400px" : "500px",
    width: layout === "horizontal" ? "100%" : "398px",
    maxWidth: layout === "horizontal" ? "700px" : "95%",
  },
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  maxWidth: "95%",
  overflowY: "auto",
}));

type FeaturePopupHeroContainer = StackLayoutProps & {
  height?: React.CSSProperties["height"];
};

export const FeaturePopupHeroContainer = styled(
  ({ layout: _l, height: _h, ...props }: FeaturePopupHeroContainer) => (
    <Stack {...props} />
  )
)(({ height = "225px", layout, theme }) => ({
  overflow: "hidden",
  height,
  [theme.breakpoints.up("sm")]: {
    height: layout === "horizontal" ? "100%" : height,
    width: layout === "horizontal" ? "50%" : undefined,
  },
  justifyContent: layout === "horizontal" ? "center" : undefined,
}));

export const FeaturePopupCloseButton = styled<
  FunctionComponent<IconButtonProps>
>((props) => (
  <IconButton data-test-id="close-tutorial-button" size="small" {...props}>
    <XCloseIcon sx={{ fill: "shades.neutral.600" }} />
  </IconButton>
))(({ theme }) => ({
  borderRadius: "8px",
  position: "absolute",
  right: 8,
  top: 8,
  backgroundColor: theme.palette.transparent.white[12],
  "& svg": {
    color: theme.palette.transparent.white[50],
  },
  "&:hover": {
    backgroundColor: theme.palette.transparent.white[50],
    "& svg": {
      color: "#FFF",
    },
  },
}));

export const FeaturePopupContent = styled(Stack)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2, 3),
  justifyContent: "space-between",
}));

export const FeaturePopupContentTitle = styled<
  FunctionComponent<TypographyProps>
>((props) => <Typography variant="h2" {...props} />)(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
}));

export const FeaturePopupContentDescription = styled<
  FunctionComponent<TypographyProps>
>((props) => <Typography variant="bodyPrimaryReg" {...props} />)(
  ({ theme }) => ({
    color: theme.palette.shades.neutral[700],
  })
);
